import { type ReactElement } from 'react';

const ACTION_PILL_KEYS = ['ap-1', 'ap-2', 'ap-3'] as const;
const META_DOT_KEYS = ['md-1', 'md-2', 'md-3'] as const;
const COMMENT_KEYS = ['c-1', 'c-2', 'c-3', 'c-4'] as const;
const EP_CARD_KEYS = [
  'ec-1',
  'ec-2',
  'ec-3',
  'ec-4',
  'ec-5',
  'ec-6',
  'ec-7',
  'ec-8',
] as const;

function PlayerSkeleton(): ReactElement {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-2xl shadow-black/35">
      <div className="skeleton-shimmer absolute inset-0" />
    </div>
  );
}

function EpisodeHeaderSkeleton(): ReactElement {
  return (
    <div className="mt-5 border-b border-border/70 pb-4">
      <div className="skeleton-shimmer h-7 w-2/3 rounded lg:h-8 lg:w-1/2" />
      <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <div className="skeleton-shimmer h-4 w-32 rounded" />
          {META_DOT_KEYS.map((key) => (
            <div key={key} className="flex items-center gap-3">
              <span className="h-1 w-1 rounded-full bg-border" />
              <div className="skeleton-shimmer h-4 w-20 rounded" />
            </div>
          ))}
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
          <div className="flex shrink-0 items-center gap-1 rounded-full border border-border bg-card/80 p-1 shadow-sm">
            <div className="skeleton-shimmer h-7 w-7 rounded-full" />
            <div className="flex overflow-hidden rounded-full border border-border bg-background">
              <div className="skeleton-shimmer h-7 w-11 rounded-none" />
              <div className="skeleton-shimmer h-7 w-11 rounded-none" />
            </div>
            <div className="skeleton-shimmer h-7 w-24 rounded-full" />
            <div className="skeleton-shimmer h-7 w-7 rounded-full" />
          </div>
          <div className="hidden h-6 w-px shrink-0 bg-border sm:block" />
          <div className="flex items-center gap-2">
            {ACTION_PILL_KEYS.map((key) => (
              <div
                key={key}
                className="skeleton-shimmer h-9 w-20 rounded-full"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCardSkeleton(): ReactElement {
  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-border/70 bg-card/70 shadow-sm backdrop-blur">
      <div className="flex w-full items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 gap-4">
          <div className="skeleton-shimmer relative h-20 w-14 shrink-0 overflow-hidden rounded-lg border border-border shadow-md" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="skeleton-shimmer h-5 w-56 max-w-full rounded" />
            <div className="skeleton-shimmer h-3 w-40 max-w-full rounded" />
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <div className="skeleton-shimmer h-5 w-10 rounded" />
              <div className="skeleton-shimmer h-5 w-10 rounded" />
              <div className="skeleton-shimmer h-5 w-24 rounded-md" />
            </div>
          </div>
        </div>
        <div className="skeleton-shimmer h-7 w-7 shrink-0 rounded-full" />
      </div>
    </div>
  );
}

function CommentsSkeleton(): ReactElement {
  return (
    <div className="mt-8">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="skeleton-shimmer h-5 w-5 rounded" />
          <div className="skeleton-shimmer h-6 w-28 rounded" />
          <div className="skeleton-shimmer h-5 w-5 rounded" />
        </div>
        <div className="skeleton-shimmer h-5 w-20 rounded-full" />
      </div>
      <div className="space-y-3 rounded-xl border border-border/70 bg-card/70 p-4">
        {COMMENT_KEYS.map((key) => (
          <div key={key} className="flex gap-3">
            <div className="skeleton-shimmer h-9 w-9 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="skeleton-shimmer h-3 w-24 rounded" />
                <div className="skeleton-shimmer h-3 w-14 rounded" />
              </div>
              <div className="skeleton-shimmer h-3 w-full rounded" />
              <div className="skeleton-shimmer h-3 w-4/5 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EpisodesPanelSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded-xl border border-border/40 bg-card/35 p-3 backdrop-blur-md sm:p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="skeleton-shimmer h-4 w-20 rounded" />
            <div className="skeleton-shimmer h-3 w-44 rounded" />
          </div>
          <div className="flex items-center gap-1 rounded-full border border-border bg-card/80 p-1">
            <div className="skeleton-shimmer h-6 w-6 rounded-full" />
            <div className="skeleton-shimmer h-6 w-6 rounded-full" />
            <div className="skeleton-shimmer h-6 w-6 rounded-full" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="skeleton-shimmer h-9 flex-1 rounded-lg" />
          <div className="skeleton-shimmer h-9 w-20 rounded-full" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="skeleton-shimmer h-3 w-32 rounded" />
          <div className="flex items-center gap-1">
            <div className="skeleton-shimmer h-7 w-7 rounded-md" />
            <div className="skeleton-shimmer h-7 w-7 rounded-md" />
            <div className="skeleton-shimmer h-7 w-7 rounded-md" />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {EP_CARD_KEYS.map((key) => (
          <div
            key={key}
            className="flex items-stretch gap-3 rounded-xl border border-border/40 bg-card/30 p-2"
          >
            <div className="skeleton-shimmer aspect-video w-32 shrink-0 rounded-lg" />
            <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
              <div className="space-y-2">
                <div className="skeleton-shimmer h-3 w-16 rounded" />
                <div className="skeleton-shimmer h-4 w-full rounded" />
                <div className="skeleton-shimmer h-3 w-3/5 rounded" />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="skeleton-shimmer h-4 w-8 rounded" />
                <div className="skeleton-shimmer h-4 w-8 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WatchSkeleton(): ReactElement {
  // Route-level RouteSkeleton owns the 250ms grace window. Paint
  // instantly so chunk-load → page-mount → internal skeleton flow
  // has no visible reset.
  return (
    <div
      className="watch-page-ambient min-h-screen w-full max-w-[100vw] overflow-x-hidden text-foreground xl:max-w-full xl:overflow-x-clip"
      style={{
        touchAction: 'pan-y',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div className="mx-auto flex w-full min-w-0 max-w-[1800px] flex-col gap-4 overflow-x-hidden px-3 pb-6 pt-2 sm:px-4 sm:pt-3 lg:px-6 xl:flex-row xl:gap-6 xl:overflow-x-visible xl:px-6 xl:pt-0">
        <section className="min-w-0 max-w-full flex-1 overflow-x-hidden xl:overflow-x-clip">
          <PlayerSkeleton />
          <EpisodeHeaderSkeleton />
          <InfoCardSkeleton />
          <CommentsSkeleton />
        </section>
        <aside className="min-w-0 shrink-0 xl:w-[400px]">
          <div className="xl:sticky xl:top-4">
            <EpisodesPanelSkeleton />
          </div>
        </aside>
      </div>
    </div>
  );
}
